import React, {
  useCallback,
  useEffect,
  memo,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import AssistantLauncher from "./AssistantLauncher";
import AssistantPanel from "./AssistantPanel";
import {
  AssistantNanooxContextRegistry,
  AssistantScappContextRegistry,
  AssistantViewContextRegistry,
} from "./viewContextStore";
import {
  generateConversationId,
  loadAssistantState,
  saveAssistantState,
} from "./assistantStorage";
import { MESSAGE_STATUS, REQUEST_PHASE, STREAM_EVENT_TYPES } from "./assistantTypes";
import { captureViewport } from "./captureViewport";
import { resolveRouteContext, isAssistantRoute } from "./routeContextResolvers";
import { streamAssistantResponse } from "./assistantClient";
import { buildVisibleDigest } from "./buildVisibleDigest";

function buildMessage(role, content, overrides = {}) {
  return {
    id:
      overrides.id ||
      (typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `msg-${Date.now()}-${Math.random().toString(16).slice(2)}`),
    role,
    content,
    status: overrides.status || MESSAGE_STATUS.DONE,
    createdAt: overrides.createdAt || new Date().toISOString(),
    metadata: overrides.metadata || {},
    sources: overrides.sources || [],
  };
}

function sameDescriptor(left, right) {
  return JSON.stringify(left || null) === JSON.stringify(right || null);
}

const StableChildren = memo(function StableChildren({ children }) {
  return children;
});

function buildProgressEvent(streamEvent, fallback = {}) {
  const toolName = streamEvent.tool ? String(streamEvent.tool).replaceAll("_", " ") : "";
  return {
    type: streamEvent.type || fallback.type || "progress",
    title:
      streamEvent.title ||
      fallback.title ||
      (toolName ? "Revisando información" : "Preparando respuesta"),
    detail:
      streamEvent.detail ||
      fallback.detail ||
      streamEvent.reason ||
      streamEvent.summary ||
      "",
    status: streamEvent.status || fallback.status || "running",
    tool: streamEvent.tool || null,
    index: streamEvent.index || null,
    createdAt: new Date().toISOString(),
  };
}

function appendProgressEvent(message, event) {
  return {
    ...message,
    metadata: {
      ...message.metadata,
      events: [
        ...(Array.isArray(message.metadata?.events) ? message.metadata.events : []),
        event,
      ],
    },
  };
}

function markAllRunningAsDone(message) {
  const events = Array.isArray(message.metadata?.events) ? message.metadata.events : [];
  if (!events.some((e) => e.status === "running")) return message;
  return {
    ...message,
    metadata: {
      ...message.metadata,
      events: events.map((e) => (e.status === "running" ? { ...e, status: "done" } : e)),
    },
  };
}

function updateLastRunningEvent(message, types, updates) {
  const events = Array.isArray(message.metadata?.events) ? [...message.metadata.events] : [];
  let idx = -1;
  for (let i = events.length - 1; i >= 0; i--) {
    if ((!types || types.includes(events[i].type)) && events[i].status === "running") {
      idx = i;
      break;
    }
  }
  if (idx < 0) return message;
  const newEvents = [...events];
  newEvents[idx] = { ...newEvents[idx], ...updates, status: "done" };
  return { ...message, metadata: { ...message.metadata, events: newEvents } };
}

export default function AssistantProvider({
  children,
  aiWebhookUrl,
  aiAssistantEnabled,
  appVersion,
  serverUrl,
}) {
  const location = useLocation();
  const persistedState = useMemo(() => loadAssistantState(), []);
  const [panelOpen, setPanelOpen] = useState(persistedState.panelOpen);
  const [messages, setMessages] = useState(persistedState.messages);
  const [conversationId, setConversationId] = useState(
    persistedState.conversationId || generateConversationId()
  );
  const [input, setInput] = useState("");
  const [requestPhase, setRequestPhase] = useState(REQUEST_PHASE.IDLE);
  const [requestNotice, setRequestNotice] = useState("");
  const [requestError, setRequestError] = useState("");
  const [autoScreenshotEnabled, setAutoScreenshotEnabled] = useState(true);
  const [includeGraphData, setIncludeGraphData] = useState(false);
  const [pendingImage, setPendingImage] = useState(null);
  const [registeredViewContext, setRegisteredViewContextState] = useState(null);
  const [registeredScappContext, setRegisteredScappContextState] = useState(null);
  const [registeredNanooxContext, setRegisteredNanooxContextState] = useState(null);
  const activeRequestRef = useRef({ controller: null, messageId: null });

  const visible =
    aiAssistantEnabled &&
    Boolean(registeredViewContext) &&
    isAssistantRoute(location.pathname);
  const webhookConfigured = Boolean(aiWebhookUrl);

  const activeViewContext = useMemo(
    () =>
      resolveRouteContext({
        pathname: location.pathname,
        title: typeof document !== "undefined" ? document.title : "",
        registeredContext: registeredViewContext,
      }),
    [location.pathname, registeredViewContext]
  );

  const setRegisteredViewContext = useCallback((nextDescriptor) => {
    setRegisteredViewContextState((currentDescriptor) =>
      sameDescriptor(currentDescriptor, nextDescriptor)
        ? currentDescriptor
        : nextDescriptor
    );
  }, []);

  const clearRegisteredViewContext = useCallback(() => {
    setRegisteredViewContextState((currentDescriptor) =>
      currentDescriptor === null ? currentDescriptor : null
    );
  }, []);

  const setRegisteredScappContext = useCallback((nextDescriptor) => {
    setRegisteredScappContextState((currentDescriptor) =>
      sameDescriptor(currentDescriptor, nextDescriptor)
        ? currentDescriptor
        : nextDescriptor
    );
  }, []);

  const clearRegisteredScappContext = useCallback(() => {
    setRegisteredScappContextState((currentDescriptor) =>
      currentDescriptor === null ? currentDescriptor : null
    );
  }, []);

  const setRegisteredNanooxContext = useCallback((nextDescriptor) => {
    setRegisteredNanooxContextState((currentDescriptor) =>
      sameDescriptor(currentDescriptor, nextDescriptor)
        ? currentDescriptor
        : nextDescriptor
    );
  }, []);

  const clearRegisteredNanooxContext = useCallback(() => {
    setRegisteredNanooxContextState((currentDescriptor) =>
      currentDescriptor === null ? currentDescriptor : null
    );
  }, []);

  useEffect(() => {
    saveAssistantState({
      conversationId,
      messages,
      panelOpen,
    });
  }, [conversationId, messages, panelOpen]);

  useEffect(() => {
    const handleOpenInsightAssistant = () => setPanelOpen(true);
    window.addEventListener("itg-insight-open-assistant", handleOpenInsightAssistant);
    return () =>
      window.removeEventListener("itg-insight-open-assistant", handleOpenInsightAssistant);
  }, []);

  const updateMessage = useCallback((messageId, updater) => {
    setMessages((currentMessages) =>
      currentMessages.map((message) =>
        message.id === messageId ? updater(message) : message
      )
    );
  }, []);

  const handleCancel = useCallback(() => {
    const activeRequest = activeRequestRef.current;
    if (!activeRequest.controller) return;

    activeRequest.controller.abort();
    setRequestPhase(REQUEST_PHASE.IDLE);
    setRequestNotice("Respuesta detenida por el usuario.");

    if (activeRequest.messageId) {
      updateMessage(activeRequest.messageId, (message) => ({
        ...message,
        status: MESSAGE_STATUS.ERROR,
        metadata: {
          ...message.metadata,
          aborted: true,
        },
      }));
    }

    activeRequestRef.current = { controller: null, messageId: null };
  }, [updateMessage]);

  const handlePasteImage = useCallback((file) => {
    if (!(file instanceof Blob)) return;

    const fileName =
      typeof file.name === "string" && file.name.trim()
        ? file.name.trim()
        : `pasted-image-${Date.now()}.png`;
    const mimeType = file.type || "application/octet-stream";

    setPendingImage({
      blob: file,
      fileName,
      mimeType,
    });
    setRequestError("");
    setRequestNotice("Imagen pegada. Se enviará con el próximo mensaje.");
    setPanelOpen(true);
  }, []);

  const handleClearPendingImage = useCallback(() => {
    setPendingImage(null);
    setRequestNotice("");
  }, []);

  const handleNewChat = useCallback(() => {
    if (requestPhase !== REQUEST_PHASE.IDLE) return;

    activeRequestRef.current = { controller: null, messageId: null };
    setConversationId(generateConversationId());
    setMessages([]);
    setInput("");
    setPendingImage(null);
    setRequestNotice("");
    setRequestError("");
    setPanelOpen(true);
  }, [requestPhase]);

  const prevServiceRef = useRef(null);
  useEffect(() => {
    const svc = activeViewContext?.service;
    if (!svc) return;
    if (prevServiceRef.current && prevServiceRef.current !== svc && messages.length > 0) {
      handleNewChat();
    }
    prevServiceRef.current = svc;
  }, [activeViewContext?.service, handleNewChat, messages.length]);

  const handleSend = useCallback(
    async (event) => {
      event.preventDefault();

      if (requestPhase !== REQUEST_PHASE.IDLE) return;

      const content = input.trim();
      if (!content && !pendingImage) return;

      if (!webhookConfigured) {
        setRequestError("El backend del asistente no está configurado.");
        return;
      }

      if (!activeViewContext) {
        setRequestError("La vista actual no tiene asistencia IA habilitada.");
        return;
      }

      const requestMessage = content || "[Imagen adjunta]";

      const imageAttachment = pendingImage
        ? {
            name: pendingImage.fileName,
            mimeType: pendingImage.mimeType,
            source: "manual",
          }
        : null;
      const userMessage = buildMessage("user", requestMessage, {
        metadata: imageAttachment ? { attachment: imageAttachment } : {},
      });
      const assistantMessage = buildMessage("assistant", "", {
        status: MESSAGE_STATUS.STREAMING,
      });
      const nextMessages = [...messages, userMessage, assistantMessage];

      setInput("");
      setRequestError("");
      setRequestNotice("");
      setPanelOpen(true);
      setMessages(nextMessages);

      let screenshot = pendingImage;
      let imageMode = "none";
      let imageError = "";

      if (pendingImage) {
        imageMode = "manual";
      } else if (autoScreenshotEnabled) {
        setRequestPhase(REQUEST_PHASE.CAPTURING);

        try {
          screenshot = await captureViewport();
          imageMode = "automatic";
        } catch (error) {
          imageError = error.message;
          setRequestNotice("La captura automática falló; se enviará solo el contexto textual.");
        }
      }

      setPendingImage(null);

      const controller = new AbortController();
      activeRequestRef.current = {
        controller,
        messageId: assistantMessage.id,
      };

      try {
        setRequestPhase(REQUEST_PHASE.SENDING);

        await streamAssistantResponse({
          endpoint: aiWebhookUrl,
          signal: controller.signal,
          payload: {
            message: requestMessage,
            conversationId,
            history: nextMessages
              .filter((message) => message.id !== assistantMessage.id)
              .map((message) => ({
                role: message.role,
                content: message.content,
              })),
            viewContext: activeViewContext,
            clientContext: {
              app: "service-manager-web",
              appVersion,
              route: location.pathname,
              serverUrl: serverUrl || "",
              timestamp: new Date().toISOString(),
              username: localStorage.getItem("username") || "",
              cargo: localStorage.getItem("cargo") || "",
            },
            imageContext: {
              mode: imageMode,
              ...(imageError ? { error: imageError } : {}),
            },
            visibleDigest: buildVisibleDigest({
              viewContext: activeViewContext,
              scappContext: registeredScappContext,
              userContext: {
                username: localStorage.getItem("username") || "",
                cargo: localStorage.getItem("cargo") || "",
              },
            }),
            scappContext:
              activeViewContext?.service === "scapp"
                ? registeredScappContext
                  ? includeGraphData
                    ? registeredScappContext
                    : (({ rawGraphSeries: _omit, ...rest }) => rest)(registeredScappContext)
                  : null
                : null,
            nanooxContext:
              activeViewContext?.service === "nanoox" ? registeredNanooxContext : null,
            screenshot,
          },
          onEvent: (streamEvent) => {
            if (!streamEvent?.type) return;

            if (streamEvent.type === STREAM_EVENT_TYPES.START) {
              setRequestPhase(REQUEST_PHASE.STREAMING);
              updateMessage(assistantMessage.id, (message) => ({
                ...message,
                metadata: {
                  ...message.metadata,
                  remoteMessageId: streamEvent.messageId,
                },
              }));
              return;
            }

            if (streamEvent.type === STREAM_EVENT_TYPES.THINKING) {
              updateMessage(assistantMessage.id, (message) => {
                const base = markAllRunningAsDone(message);
                return appendProgressEvent(
                  base,
                  buildProgressEvent(streamEvent, {
                    type: "thinking",
                    title: "Analizando tu consulta",
                    detail: "",
                    status: "running",
                  })
                );
              });
              return;
            }

            if (streamEvent.type === STREAM_EVENT_TYPES.PLAN) {
              updateMessage(assistantMessage.id, (message) => {
                const nextMessage = {
                  ...message,
                  metadata: {
                    ...message.metadata,
                    plan: streamEvent,
                  },
                };
                return appendProgressEvent(
                  nextMessage,
                  buildProgressEvent(streamEvent, {
                    type: "plan",
                    title: "Plan definido",
                    detail:
                      streamEvent.classification?.reason ||
                      streamEvent.classification?.mode ||
                      "Ya definí cómo abordar la consulta.",
                    status: "done",
                  })
                );
              });
              return;
            }

            if (streamEvent.type === STREAM_EVENT_TYPES.TOOL_CALL) {
              updateMessage(assistantMessage.id, (message) => {
                const base = updateLastRunningEvent(message, ["thinking"], {});
                return appendProgressEvent(
                  base,
                  buildProgressEvent(streamEvent, {
                    type: "tool_call",
                    title: "Consultando información",
                    detail: "",
                    status: "running",
                  })
                );
              });
              return;
            }

            if (streamEvent.type === STREAM_EVENT_TYPES.TOOL_RESULT) {
              updateMessage(assistantMessage.id, (message) =>
                updateLastRunningEvent(message, ["tool_call"], {})
              );
              return;
            }

            if (streamEvent.type === STREAM_EVENT_TYPES.DELTA) {
              setRequestPhase(REQUEST_PHASE.STREAMING);
              updateMessage(assistantMessage.id, (message) => ({
                ...message,
                content: `${message.content}${streamEvent.content || ""}`,
                status: MESSAGE_STATUS.STREAMING,
              }));
              return;
            }

            if (streamEvent.type === STREAM_EVENT_TYPES.CONTEXT) {
              updateMessage(assistantMessage.id, (message) => ({
                ...message,
                metadata: {
                  ...message.metadata,
                  context: streamEvent.context || null,
                },
              }));
              return;
            }

            if (streamEvent.type === STREAM_EVENT_TYPES.SOURCES) {
              updateMessage(assistantMessage.id, (message) => ({
                ...message,
                sources: Array.isArray(streamEvent.sources)
                  ? streamEvent.sources
                  : [],
              }));
              return;
            }

            if (streamEvent.type === STREAM_EVENT_TYPES.FILE_EXPORT) {
              updateMessage(assistantMessage.id, (message) => ({
                ...message,
                metadata: {
                  ...message.metadata,
                  exports: [
                    ...(Array.isArray(message.metadata?.exports) ? message.metadata.exports : []),
                    {
                      filename: streamEvent.filename,
                      data: streamEvent.data,
                      mimeType:
                        streamEvent.mimeType ||
                        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                    },
                  ],
                },
              }));
              return;
            }

            if (streamEvent.type === STREAM_EVENT_TYPES.DASHBOARD_PATCH) {
              if (typeof window !== "undefined") {
                window.dispatchEvent(
                  new CustomEvent("itg-insight-dashboard-patch", {
                    detail: streamEvent,
                  })
                );
              }
              updateMessage(assistantMessage.id, (message) => ({
                ...message,
                metadata: {
                  ...message.metadata,
                  dashboardPatches: [
                    ...(Array.isArray(message.metadata?.dashboardPatches)
                      ? message.metadata.dashboardPatches
                      : []),
                    streamEvent,
                  ],
                },
              }));
              return;
            }

            if (streamEvent.type === STREAM_EVENT_TYPES.WARNING) {
              updateMessage(assistantMessage.id, (message) => {
                const nextMessage = {
                  ...message,
                  metadata: {
                    ...message.metadata,
                    warnings: [
                      ...(Array.isArray(message.metadata?.warnings)
                        ? message.metadata.warnings
                        : []),
                      streamEvent.warning || "Warning",
                    ],
                  },
                };
                return appendProgressEvent(
                  nextMessage,
                  buildProgressEvent(streamEvent, {
                    type: "warning",
                    title: "Paso omitido",
                    detail: streamEvent.warning || "Continuaré con la evidencia disponible.",
                    status: "warning",
                  })
                );
              });
              return;
            }

            if (streamEvent.type === STREAM_EVENT_TYPES.ERROR) {
              updateMessage(assistantMessage.id, (message) => ({
                ...message,
                content:
                  message.content ||
                  streamEvent.error ||
                  "Se produjo un error durante el streaming.",
                status: MESSAGE_STATUS.ERROR,
                metadata: {
                  ...message.metadata,
                  error: streamEvent.error || "Unknown streaming error",
                },
              }));
              setRequestPhase(REQUEST_PHASE.IDLE);
              setRequestError(streamEvent.error || "Error en el streaming.");
              return;
            }

            if (streamEvent.type === STREAM_EVENT_TYPES.DONE) {
              updateMessage(assistantMessage.id, (message) =>
                appendProgressEvent(
                  markAllRunningAsDone({
                    ...message,
                    content: streamEvent.final || message.content,
                    status: MESSAGE_STATUS.DONE,
                  }),
                  {
                    type: "done",
                    title: "Respuesta lista",
                    detail: "",
                    status: "done",
                    createdAt: new Date().toISOString(),
                  }
                )
              );
              setRequestPhase(REQUEST_PHASE.IDLE);
              return;
            }
          },
        });

        updateMessage(assistantMessage.id, (message) =>
          message.status === MESSAGE_STATUS.STREAMING
            ? {
                ...message,
                status: MESSAGE_STATUS.DONE,
              }
            : message
        );
      } catch (error) {
        if (error.name !== "AbortError") {
          updateMessage(assistantMessage.id, (message) => ({
            ...message,
            content:
              message.content ||
              "No fue posible obtener una respuesta desde el backend del asistente.",
            status: MESSAGE_STATUS.ERROR,
            metadata: {
              ...message.metadata,
              error: error.message,
            },
          }));
          setRequestError(error.message);
        }
      } finally {
        activeRequestRef.current = { controller: null, messageId: null };
        setRequestPhase(REQUEST_PHASE.IDLE);
      }
    },
    [
      activeViewContext,
      aiWebhookUrl,
      autoScreenshotEnabled,
      appVersion,
      conversationId,
      input,
      location.pathname,
      messages,
      pendingImage,
      requestPhase,
      registeredScappContext,
      registeredNanooxContext,
      serverUrl,
      updateMessage,
      webhookConfigured,
    ]
  );

  const handleRestoreConversation = useCallback(
    (convId, convMessages) => {
      if (requestPhase !== REQUEST_PHASE.IDLE) return;
      setConversationId(convId);
      setMessages(convMessages);
      setInput("");
      setRequestError("");
      setRequestNotice("");
    },
    [requestPhase]
  );

  const portal =
    typeof document !== "undefined"
      ? createPortal(
          <>
            {visible && (
              <>
                <AssistantPanel
                  open={panelOpen}
                  messages={messages}
                  input={input}
                  onInputChange={setInput}
                  onClose={() => setPanelOpen(false)}
                  onSend={handleSend}
                  onCancel={handleCancel}
                  onPasteImage={handlePasteImage}
                  onClearPendingImage={handleClearPendingImage}
                  onNewChat={handleNewChat}
                  pendingImageName={pendingImage?.fileName || ""}
                  autoScreenshotEnabled={autoScreenshotEnabled}
                  onAutoScreenshotEnabledChange={setAutoScreenshotEnabled}
                  includeGraphData={includeGraphData}
                  onIncludeGraphDataChange={setIncludeGraphData}
                  requestPhase={requestPhase}
                  requestNotice={requestNotice}
                  requestError={requestError}
                  webhookConfigured={webhookConfigured}
                  viewContext={activeViewContext}
                  aiWebhookUrl={aiWebhookUrl}
                  onRestoreConversation={handleRestoreConversation}
                />
                <AssistantLauncher
                  onClick={() => setPanelOpen(true)}
                  disabled={!visible}
                  hidden={panelOpen}
                  hasStreamingMessage={
                    requestPhase === REQUEST_PHASE.STREAMING ||
                    requestPhase === REQUEST_PHASE.SENDING
                  }
                />
              </>
            )}
          </>,
          document.body
        )
      : null;

  const registryValue = useMemo(
    () => ({
      setRegisteredViewContext,
      clearRegisteredViewContext,
    }),
    [clearRegisteredViewContext, setRegisteredViewContext]
  );

  const scappRegistryValue = useMemo(
    () => ({
      setRegisteredScappContext,
      clearRegisteredScappContext,
    }),
    [clearRegisteredScappContext, setRegisteredScappContext]
  );

  const nanooxRegistryValue = useMemo(
    () => ({
      setRegisteredNanooxContext,
      clearRegisteredNanooxContext,
    }),
    [clearRegisteredNanooxContext, setRegisteredNanooxContext]
  );

  return (
    <AssistantViewContextRegistry.Provider value={registryValue}>
      <AssistantScappContextRegistry.Provider value={scappRegistryValue}>
        <AssistantNanooxContextRegistry.Provider value={nanooxRegistryValue}>
          <StableChildren>{children}</StableChildren>
          {portal}
        </AssistantNanooxContextRegistry.Provider>
      </AssistantScappContextRegistry.Provider>
    </AssistantViewContextRegistry.Provider>
  );
}
